import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';
import { API } from '../App';

const trackEvent = (event_type, data = {}) => {
  axios.post(`${API}/admin/analytics/track`, { event_type, data }).catch(() => {});
};

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    // Track unique page view once per session
    if (!sessionStorage.getItem('pv-tracked')) {
      trackEvent('page_view', { path: window.location.pathname, standalone: isStandalone });
      sessionStorage.setItem('pv-tracked', '1');
    }

    // If already installed (running in standalone), report once per device
    if (isStandalone) {
      setIsInstalled(true);
      if (!localStorage.getItem('pwa-install-reported')) {
        trackEvent('pwa_install', { source: 'standalone-detected', platform: navigator.platform });
        localStorage.setItem('pwa-install-reported', Date.now().toString());
      }
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    const installedHandler = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      if (!localStorage.getItem('pwa-install-reported')) {
        trackEvent('pwa_install', { source: 'appinstalled-event', platform: navigator.platform });
        localStorage.setItem('pwa-install-reported', Date.now().toString());
      }
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-card border border-border rounded-2xl shadow-lg p-4 z-50 animate-fade-in">
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shrink-0">
          <span className="text-white font-bold text-lg">CK</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-medium text-foreground">Install App</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add Christ Kingdom to your home screen for quick access
          </p>
        </div>
      </div>
      
      <Button onClick={handleInstall} className="w-full mt-4" data-testid="install-app-button">
        <Download className="h-4 w-4 mr-2" />
        Install
      </Button>
    </div>
  );
}
